// Lixeira: só admin/gerente. Pedidos que a vendedora excluiu (soft-delete)
// ficam aqui, podem ser restaurados ou apagados definitivamente.

import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { getPedidosDeletados, restaurarPedido, apagarPedidoDefinitivo } from '../utils/storage'
import { fmtMoeda } from '../utils/fmt'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { ShieldAlert, Trash2, RotateCcw, Archive, AlertCircle } from 'lucide-react'

export function Lixeira() {
  const { profile } = useAuth()
  const [lista, setLista] = useState([])
  const [confirmando, setConfirmando] = useState(null) // {tipo: 'restaurar'|'apagar', pedido}
  const ehGerente = profile?.role === 'admin' || profile?.role === 'gerente'

  useEffect(() => {
    if (ehGerente) setLista(getPedidosDeletados())
  }, [ehGerente])

  function recarregar() { setLista(getPedidosDeletados()) }

  function fazerRestaurar(id) {
    restaurarPedido(id)
    recarregar()
    setConfirmando(null)
  }
  function fazerApagar(id) {
    apagarPedidoDefinitivo(id)
    recarregar()
    setConfirmando(null)
  }

  if (!ehGerente) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <ShieldAlert className="mx-auto text-red-500 mb-3" size={48} />
        <h1 className="text-xl font-bold text-slate-800 mb-2">Acesso restrito</h1>
        <p className="text-sm text-slate-500">
          A lixeira só é acessível pra usuários com <code className="bg-slate-100 px-1 rounded">role = admin</code> ou <code className="bg-slate-100 px-1 rounded">gerente</code>.
        </p>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <Archive size={20} className="text-amber-600" />
          Lixeira
        </h1>
        <p className="text-sm text-slate-400 mt-0.5">
          Pedidos excluídos ficam aqui — restaure ou apague definitivamente.
        </p>
      </div>

      {lista.length === 0 ? (
        <div className="card p-12 text-center">
          <Archive className="mx-auto text-slate-200 mb-3" size={40} />
          <p className="text-sm text-slate-400">Nenhum pedido excluído.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {lista.map(p => (
            <div key={p.id} className="card p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-bold text-slate-800">{p.numero}</p>
                    {p.status && (
                      <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                        {p.status}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-700 mt-1">{p.cliente?.nome || '—'}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Prazo: {p.prazo || '—'} · Valor: {fmtMoeda(Number(p.valorTotal) || 0)}
                  </p>
                  <p className="text-xs text-red-700 mt-2 flex items-center gap-1.5">
                    <AlertCircle size={11} />
                    Excluído por <strong>{p.deletadoPor}</strong>
                    {' · '}
                    {(() => {
                      try { return format(new Date(p.deletadoEm), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }) }
                      catch { return p.deletadoEm }
                    })()}
                  </p>
                  {p.motivoExclusao && (
                    <p className="text-xs italic text-slate-500 mt-1">"{p.motivoExclusao}"</p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => setConfirmando({ tipo: 'restaurar', pedido: p })}
                    className="btn-secondary text-xs"
                  >
                    <RotateCcw size={12} />
                    Restaurar
                  </button>
                  {profile?.role === 'admin' && (
                    <button
                      onClick={() => setConfirmando({ tipo: 'apagar', pedido: p })}
                      className="btn-secondary text-xs text-red-500 hover:bg-red-50"
                      title="Apaga DEFINITIVAMENTE — sem volta"
                    >
                      <Trash2 size={12} />
                      Apagar
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmando && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setConfirmando(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="font-bold text-slate-800 mb-2">
              {confirmando.tipo === 'restaurar' ? 'Restaurar pedido?' : 'Apagar definitivamente?'}
            </h3>
            <p className="text-sm text-slate-500 mb-5">
              {confirmando.tipo === 'restaurar' ? (
                <>Pedido <strong>{confirmando.pedido.numero}</strong> volta pra aba Pedidos e todas as outras telas.</>
              ) : (
                <>Pedido <strong>{confirmando.pedido.numero}</strong> será apagado <strong>PARA SEMPRE</strong>. Sem volta.</>
              )}
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmando(null)} className="btn-secondary flex-1 justify-center">Cancelar</button>
              {confirmando.tipo === 'restaurar' ? (
                <button onClick={() => fazerRestaurar(confirmando.pedido.id)} className="btn-primary flex-1 justify-center">Restaurar</button>
              ) : (
                <button onClick={() => fazerApagar(confirmando.pedido.id)} className="btn-danger flex-1 justify-center">Apagar</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
